import type { OsFamily } from '@occ/contracts';
import { AnimatePresence, m } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  FileCode2,
  Terminal as TerminalIcon,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  DEFAULT_TIMEOUT_SECONDS,
  SHELLS,
  defaultShellFor,
  runScript,
  type ScriptResult,
  type ScriptShell,
} from '../data/scripts';
import { ErrorNotice } from './ErrorNotice';
import { Badge, Button, Card, ConfirmDialog, Field, Notice, Select, Textarea } from './ui';

/**
 * Ejecución de scripts (Módulo 6.1, RF-10).
 *
 * `script.run` está marcada como destructiva en el catálogo y lo es: un script
 * arbitrario en la caja de un cliente puede hacer cualquier cosa. De ahí la
 * confirmación previa que enseña el intérprete, el nodo y las primeras líneas de
 * lo que se va a ejecutar — leer el script antes de lanzarlo es la única defensa
 * real contra pegar el comando equivocado en la ventana equivocada.
 *
 * **La salida no es incremental** porque el contrato no lo es: `script.run`
 * devuelve `stdout`, `stderr`, `exitCode` y `truncated` de una vez, al terminar.
 * Mientras corre se enseña el tiempo transcurrido, que sí es información real, y
 * se dice que la salida llega al final. Fingir un avance sería peor que no
 * tenerlo: el técnico decidiría si esperar o cortar basándose en una animación.
 */

type Phase =
  | { kind: 'idle' }
  | { kind: 'running'; startedAt: number }
  | { kind: 'done'; result: ScriptResult; elapsedMs: number }
  | { kind: 'failed'; error: unknown };

const PLACEHOLDERS: Record<ScriptShell, string> = {
  powershell: 'Get-Service -Name Spooler | Select-Object Status, StartType',
  bash: 'systemctl status nginx --no-pager',
  sh: 'df -h /',
};

/**
 * Intérprete que sugiere la extensión de un archivo importado. Solo cubre las
 * extensiones que no dejan lugar a dudas — un `.txt` no dice nada del
 * intérprete, así que se deja el que ya estaba elegido.
 */
const SHELL_BY_EXTENSION: Record<string, ScriptShell> = {
  ps1: 'powershell',
  psm1: 'powershell',
  sh: 'bash',
  bash: 'bash',
};

const extensionOf = (filename: string): string | undefined => filename.split('.').pop();

export function ScriptRunner({
  nodeId,
  alias,
  online,
  osFamily,
  disabledReason,
}: {
  nodeId: string;
  alias: string;
  online: boolean;
  osFamily: OsFamily | null;
  /** Motivo por el que no se puede ejecutar. `null` = se puede. */
  disabledReason: string | null;
}) {
  const [shell, setShell] = useState<ScriptShell>(() => defaultShellFor(osFamily));
  const [script, setScript] = useState('');
  const [timeoutSeconds, setTimeoutSeconds] = useState(DEFAULT_TIMEOUT_SECONDS);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [confirming, setConfirming] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [importedFile, setImportedFile] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const filePicker = useRef<HTMLInputElement | null>(null);

  // El nodo puede reportar su sistema después de abrir la ficha; entonces el
  // intérprete por omisión cambia, pero solo si el operador no lo tocó todavía.
  const shellTouched = useRef(false);
  useEffect(() => {
    if (!shellTouched.current) setShell(defaultShellFor(osFamily));
  }, [osFamily]);

  // Importar un archivo es más rápido que teclear o pegar un script largo, y
  // menos propenso a que el editor del sistema operativo cambie saltos de línea
  // por el camino. Se lee entero en memoria porque `script.run` viaja inline: no
  // hay streaming posible para el contenido de un script.
  const importFile = async (file: File) => {
    const content = await file.text();
    setScript(content);
    setImportedFile(file.name);
    const suggestion = SHELL_BY_EXTENSION[extensionOf(file.name)?.toLowerCase() ?? ''];
    if (suggestion) {
      shellTouched.current = true;
      setShell(suggestion);
    }
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const [file] = Array.from(event.dataTransfer.files);
    if (file) void importFile(file);
  };

  // Reloj del tiempo transcurrido. Un segundo de resolución basta y no repinta
  // la pantalla treinta veces por segundo mientras el script corre. Es lo único
  // que se puede enseñar en vivo: la salida no llega hasta el final.
  useEffect(() => {
    if (phase.kind !== 'running') return;
    const startedAt = phase.startedAt;
    setElapsedSeconds(0);
    const timer = setInterval(
      () => setElapsedSeconds(Math.round((Date.now() - startedAt) / 1_000)),
      1_000,
    );
    return () => clearInterval(timer);
  }, [phase]);

  const execute = async () => {
    setConfirming(false);
    const startedAt = Date.now();
    setPhase({ kind: 'running', startedAt });
    try {
      const result = await runScript(nodeId, script, shell, timeoutSeconds);
      setPhase({ kind: 'done', result, elapsedMs: Date.now() - startedAt });
    } catch (error) {
      setPhase({ kind: 'failed', error });
    }
  };

  const running = phase.kind === 'running';
  const blocked = disabledReason ?? (online ? null : 'El nodo no está conectado');

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <TerminalIcon className="size-4 opacity-70" />
          Ejecutar script
        </span>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            disabled={running}
            onClick={() => filePicker.current?.click()}
          >
            <Upload className="size-3.5" />
            Importar archivo
          </Button>
          <Button
            variant="primary"
            disabled={running || blocked !== null || script.trim() === ''}
            title={blocked ?? undefined}
            onClick={() => setConfirming(true)}
          >
            {running ? `Ejecutando… ${elapsedSeconds} s` : 'Ejecutar'}
          </Button>
        </div>
      }
    >
      <input
        ref={filePicker}
        type="file"
        accept=".ps1,.psm1,.sh,.bash,.txt,text/plain"
        className="hidden"
        onChange={(event) => {
          const [file] = Array.from(event.target.files ?? []);
          if (file) void importFile(file);
          event.target.value = '';
        }}
      />

      <div className="grid gap-3">
        {blocked && <Notice tone="warn">{blocked}</Notice>}

        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="Intérprete"
            value={shell}
            onChange={(event) => {
              shellTouched.current = true;
              setShell(event.target.value as ScriptShell);
            }}
          >
            {SHELLS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Field
            label="Tiempo máximo (s)"
            type="number"
            min={1}
            max={3600}
            value={timeoutSeconds}
            hint="El agente corta el proceso al pasarse. Tope del contrato: 3600."
            onChange={(event) => setTimeoutSeconds(Number(event.target.value) || 1)}
          />
          {osFamily && <Badge>nodo {osFamily}</Badge>}
        </div>

        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded transition ${
            dragging ? 'ring-2 ring-[var(--occ-brand)] ring-offset-1' : ''
          }`}
        >
          <Textarea
            label="Script"
            rows={10}
            spellCheck={false}
            value={script}
            placeholder={PLACEHOLDERS[shell]}
            onChange={(event) => {
              setScript(event.target.value);
              setImportedFile(null);
            }}
            hint="Se manda como script en línea. Arrastra un archivo .ps1/.sh/.txt aquí, o pégalo a mano."
          />
        </div>

        <AnimatePresence>
          {importedFile && (
            <m.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-2 text-xs opacity-70"
            >
              <FileCode2 className="size-3.5" />
              <span>
                Importado de <strong className="font-medium">{importedFile}</strong>
              </span>
              <button
                type="button"
                onClick={() => setImportedFile(null)}
                className="rounded p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                aria-label="Descartar aviso de importación"
              >
                <X className="size-3.5" />
              </button>
            </m.div>
          )}
        </AnimatePresence>

        {running && (
          <Notice tone="pending" title={`Ejecutando en ${alias} desde hace ${elapsedSeconds} s`}>
            La salida llega completa al terminar: `script.run` del catálogo es petición y respuesta,
            no un stream. Para ver salida a medida que ocurre, la terminal sí lo es.
          </Notice>
        )}

        {phase.kind === 'failed' && (
          <ErrorNotice
            error={phase.error}
            what="Ejecutar el script"
            pendingHint="Necesita la capacidad «exec» en el agente y el túnel abierto hasta el nodo."
          />
        )}

        <AnimatePresence>
          {phase.kind === 'done' && (
            <m.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ScriptOutput result={phase.result} elapsedMs={phase.elapsedMs} />
            </m.div>
          )}
        </AnimatePresence>
      </div>

      <ConfirmDialog
        open={confirming}
        title="Ejecutar un script en un nodo de producción"
        confirmLabel="Ejecutar ahora"
        onCancel={() => setConfirming(false)}
        onConfirm={() => void execute()}
      >
        <p>
          Se va a ejecutar en <strong>{alias}</strong> con <strong>{shell}</strong>, con un tope de{' '}
          {timeoutSeconds} s.
        </p>
        <pre className="max-h-40 overflow-auto rounded bg-black/5 dark:bg-white/5 p-2 text-xs leading-5">
          {script.trim()}
        </pre>
        <Notice tone="warn">
          Queda en la bitácora a tu nombre. El agente lo ejecuta con los privilegios del servicio,
          que en Windows es <code>SYSTEM</code>.
        </Notice>
      </ConfirmDialog>
    </Card>
  );
}

function ScriptOutput({ result, elapsedMs }: { result: ScriptResult; elapsedMs: number }) {
  const failed = result.exitCode !== 0;
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={failed ? 'warn' : 'ok'}>
          <span className="inline-flex items-center gap-1">
            {failed ? <XCircle className="size-3" /> : <CheckCircle2 className="size-3" />}
            código de salida {result.exitCode}
          </span>
        </Badge>
        <span className="inline-flex items-center gap-1 opacity-60">
          <Clock className="size-3" />
          {(elapsedMs / 1_000).toFixed(1)} s
        </span>
        {result.truncated && <Badge tone="warn">salida truncada por el agente</Badge>}
      </div>

      <OutputPane label="stdout" text={result.stdout} />
      {result.stderr.trim() !== '' && <OutputPane label="stderr" text={result.stderr} error />}
    </div>
  );
}

function OutputPane({ label, text, error }: { label: string; text: string; error?: boolean }) {
  return (
    <div className="grid gap-1">
      <div className="text-xs uppercase tracking-wide opacity-50">{label}</div>
      <pre
        className={`max-h-72 overflow-auto rounded p-3 text-xs leading-5 ${
          error ? 'bg-red-500/10 text-red-700 dark:text-red-300' : 'bg-black/5 dark:bg-white/5'
        }`}
      >
        {text === '' ? '(vacío)' : text}
      </pre>
    </div>
  );
}
